import { isFunction, snakeCase } from 'lodash';
import { assign, createMachine } from 'xstate';
import { LDClient, LDFlags } from '~/providers';
import { Document } from '~/screens/lmi-proof-docs-upload/component/document-details.component';
import { LMIProofDoc } from '~/screens/lmi-proof-docs-upload/component/lmi-proof-docs-upload.component';
import { fetchUtilityAccount, uploadLMIProofDoc } from '~/services';
import { fetchTermsBase } from '~/services/fetch-vnm-terms/utils';
import { updateUser } from '~/services/update-user-meta-data';
import { ContractVersion } from '~/typings/context';
import { LmiProofDocUploadDocumentType, LmiProofDocUploadRelationship } from '~/typings/graphql';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { sendToSegment } from '~/utils/tracking';
import { setError } from '../utils/actions';
import { contextCreator } from '../utils/context';
import {
  LmiReviewContext,
  LmiReviewDoneData,
  LmiReviewEvent,
  LmiReviewServices,
  LmiReviewStates,
} from './types';

export const lmiReviewMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as LmiReviewContext,
        events: {} as LmiReviewEvent,
        services: {} as LmiReviewServices,
      },
      context: {
        errors: {},
        funnelPacket: {} as LmiReviewContext['funnelPacket'],
        showLmi: false,
        vnmSolarProjectContracts: [] as ContractVersion[],
        proofDocs: [] as LMIProofDoc[],
        documentTypes: [] as string[],
        contractTags: [] as string[],
      },
      id: 'lmiReviewMachine',
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: { target: 'lmiReview' },
        },
        lmiReview: {
          initial: 'fetchingUtilityAccount',
          states: {
            fetchingUtilityAccount: {
              invoke: {
                id: 'fetchUtilityAccount',
                src: 'fetchUtilityAccount',
                onDone: [{ target: 'fetching', actions: 'setUtilityAccount' }],
                onError: { target: 'fetching', actions: 'setLmiReviewError' },
              },
            },
            fetching: {
              invoke: {
                id: 'fetchTerms',
                src: 'fetchTerms',
                onDone: [
                  {
                    target: 'idle',
                    actions: 'saveFetchTermsData',
                  },
                ],
                onError: { target: 'idle', actions: 'setLmiReviewError' },
              },
            },
            idle: {
              on: {
                UPLOAD_PROOFS_AND_GENERATE_ENVELOPE: {
                  actions: [
                    'setProofDocs',
                    'setLmiStatus',
                    'setSelfAttestationData',
                    'trackLmiAttestation',
                  ],
                  target: 'saveProofDocs',
                },
                SKIP_LMI_REVIEW: {
                  actions: ['setLmiStatus', 'setSelfAttestationData', 'trackSkipLmiReview'],
                  target: '#updateUser',
                },
              },
            },
            saveProofDocs: {
              invoke: {
                id: 'saveProofDocs',
                src: 'saveProofDocs',
                onDone: { target: '#updateUser' },
                onError: { target: '#updateUser', actions: 'setLmiReviewError' },
              },
            },
          },
        },
        updateUser: {
          id: 'updateUser',
          invoke: {
            id: 'updateUser',
            src: 'updateUser',
            onDone: { target: '#completed' },
            onError: { target: '#completed', actions: 'setLmiReviewError' },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          data: (context): LmiReviewDoneData => ({
            docuSignContracts: context.docuSignContracts,
            vnmSolarProjectContracts: context.vnmSolarProjectContracts,
            selfAttestationData: context.selfAttestationData,
            lmiStatus: context.lmiStatus,
          }),
          entry: 'trackCompleted',
        },
      },
    },
    {
      guards: {},
      actions: {
        saveFetchTermsData: assign((_context, event) => {
          return {
            docuSignContracts: event.data.docuSignContracts,
            showLmi: event.data.showLmi,
            vnmSolarProjectContracts: event.data.vnmSolarProjectContracts,
          };
        }),
        setProofDocs: assign((_context, event) => {
          return {
            proofDocs: event.data?.proofDocs,
          };
        }),
        setLmiStatus: assign((_context, event) => {
          return {
            lmiStatus: event.data?.lmiStatus,
          };
        }),
        setSelfAttestationData: assign((_context, event) => {
          return {
            selfAttestationData: event.data?.SelfAttestationData,
          };
        }),
        setUtilityAccount: assign((_context, event) => {
          return {
            utilityAccount: event.data.utilityAccount,
          };
        }),
        setLmiReviewError: assign((context, event) => ({
          errors: setError<LmiReviewStates>('review', context, event),
        })),
        trackCompleted: context => {
          sendToSegment({
            type: 'lmi_review_step_completed',
            contractIds: context.vnmSolarProjectContracts?.map(c => c.id) || [],
            funnelPacket: context.funnelPacket,
            selfAttestationData: context.selfAttestationData || null,
          });
        },
        trackLmiAttestation: context => {
          sendToSegment({
            type: 'lmi_attestation_submitted',
            selfAttestationData: context.selfAttestationData || null,
            documentTypes: context.documentTypes,
            funnelPacket: context.funnelPacket,
          });
        },
        trackSkipLmiReview: context => {
          sendToSegment({
            type: 'lmi_review_step_skipped',
            funnelPacket: context.funnelPacket,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'lmi_review_step_started',
            funnelPacket: context.funnelPacket,
          });
        },
      },
      services: {
        saveProofDocs: async (context, event) => {
          const utilityAccountId = context.funnelPacket.utilityAccount.id;
          const enableManualProofCapture = LDClient.variation(LDFlags.enableManualProofCapture);
          const { proofDocs } = event.data;
          let results: LMIProofDoc[] = [];
          for (const doc of proofDocs) {
            let result: LMIProofDoc | undefined = {} as LMIProofDoc;
            try {
              if (!enableManualProofCapture) {
                const { documentType, image } = doc as LMIProofDoc;
                result = await uploadLMIProofDoc({
                  utilityAccountId,
                  documentType,
                  file: image,
                  base64Image: image.base64Image!,
                });
              } else {
                const { documentType, files, relationship, name } = doc as Document;
                for (const eachFile of files) {
                  const { proofType, file } = eachFile;
                  result = await uploadLMIProofDoc({
                    utilityAccountId,
                    documentType,
                    file,
                    base64Image: file.base64Image!,
                    name,
                    proofType: snakeCase(proofType) as LmiProofDocUploadDocumentType,
                    relationship: relationship.split('-')[0] as LmiProofDocUploadRelationship,
                  });
                }
              }
              if (result.image.error) {
                throw new Error(result.image.error);
              }
              results.push(result);
            } catch (error) {
              results = [];
              throw new Error(error);
            }
          }

          return results;
        },
        fetchTerms: async context => {
          const contractTags = isFunction(context.contractTags)
            ? context.contractTags(context)
            : context.contractTags;
          const {
            docuSignContracts,
            unsignedSolarContracts,
            showLmiFromTerms: showLmi,
          } = await fetchTermsBase({
            stateId: context.funnelPacket.utility.state.id,
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            contractTags,
          });

          const data = await fetchContractLanguage(
            docuSignContracts,
            unsignedSolarContracts,
            showLmi,
          );
          return {
            docuSignContracts: data.docuSignContracts as ContractVersion[],
            vnmSolarProjectContracts: data.vnmSolarProjectContracts as ContractVersion[],
            showLmi: data.showLmi!,
          };
        },
        fetchUtilityAccount: async (context: LmiReviewContext) => {
          const utilityAccountId = context.funnelPacket.utilityAccount.id;
          const data = await fetchUtilityAccount({ utilityAccountId });
          return data;
        },
        updateUser: async (context: LmiReviewContext) => {
          const data = await updateUser(context.funnelPacket.user.id, {
            lmiSelfAttestation: {
              selfAttestationData: context.selfAttestationData || null,
              lmiStatus: context.lmiStatus,
            },
          });
          return data;
        },
      },
    },
  );

export const lmiReviewContext = contextCreator<LmiReviewContext, 'funnelPacket'>(lmiReviewMachine);
